/**
 * Markup del Greenie Board — Fase 5g.3.
 *
 * El tablero se pinta sobre el asset `greenie-board.webp` (pizarra con
 * marco y título ya horneados). El contenido se compone de:
 *  - Leyenda de colores por calificación LSO + fecha de actualización.
 *  - Cuerpo: una fila por piloto. Cada fila = callsign fijo a la
 *    izquierda + tira de pases en orden cronológico (más reciente a la
 *    derecha), con scroll horizontal para el historial antiguo.
 *
 * Cada pase es un cuadro dividido en bandas horizontales (una por
 * intento, la más antigua arriba, el trap final abajo). El color de
 * cada banda sale de la calificación LSO; la silueta de avión va en
 * negro. Cuando hay trap, el número de cable va en la esquina inferior
 * derecha; un `_OK_` final añade una estrella.
 *
 * Cada cuadro de pase es clickable (5g.4): abre el modal de detalle con
 * la nota del LSO decodificada. Lleva `data-pilot`/`data-pass` para que
 * el mount localice el pase en los datos cargados.
 */

import type {
  GreenieBoardPilot,
  GreeniePass,
} from '@51esc/shared';
import { silhouetteUrl } from './silhouette.js';

// ─── Calificaciones LSO ────────────────────────────────────────────────

/** Metadatos de presentación de una calificación LSO. */
interface GradeInfo {
  /** Clase CSS que fija `--g` (color del grade) en `greenie-board.css`. */
  cls: string;
  /** Etiqueta legible para la leyenda y los tooltips. */
  label: string;
}

/**
 * Tabla de calificaciones que DCS emite en `GRADE:` (ver
 * `api/src/reports/parser/lso.ts`). Cualquier grado fuera de esta
 * tabla cae a `gb-g-unknown` — fallback honesto, sin inventar color.
 */
const GRADE_TABLE: Record<string, GradeInfo> = {
  _OK_: { cls: 'gb-g-oku', label: 'OK destacado — pase perfecto' },
  OK: { cls: 'gb-g-ok', label: 'OK — buen pase' },
  '(OK)': { cls: 'gb-g-fair', label: 'OK justo' },
  '--': { cls: 'gb-g-none', label: 'Sin nota — pase pobre' },
  B: { cls: 'gb-g-bolter', label: 'Bolter — no enganchó cable' },
  WO: { cls: 'gb-g-wo', label: 'Wave-off — apontaje abortado' },
  OWO: { cls: 'gb-g-wo', label: 'Own wave-off — abortado por el piloto' },
  C: { cls: 'gb-g-cut', label: 'Cut — pase peligroso' },
};

/** Orden de mejor a peor para la leyenda. */
const LEGEND_ORDER: string[] = ['_OK_', 'OK', '(OK)', '--', 'B', 'WO', 'C'];

/**
 * Todas las calificaciones que un admin puede asignar a un intento desde
 * el modal de edición (Fase 5g.5d), en orden de mejor a peor. Coinciden
 * con las claves de `LSO_GRADE_POINTS` (api/src/reports/parser/lso.ts).
 */
export const EDITABLE_GRADES: readonly string[] = [
  '_OK_',
  'OK',
  '(OK)',
  'B',
  '--',
  'WO',
  'OWO',
  'C',
];

/** Devuelve los metadatos de un grade, con fallback para grados sin tabular. */
function gradeInfo(grade: string): GradeInfo {
  return (
    GRADE_TABLE[grade] ?? {
      cls: 'gb-g-unknown',
      label: `Calificación sin tabular (${grade})`,
    }
  );
}

// ─── Shell del tablero ─────────────────────────────────────────────────

/**
 * Estructura estática del tablero. El cuerpo (`[data-zone="rows"]`) y
 * la fecha (`[data-zone="updated"]`) los rellena `mount.ts` tras el
 * fetch. El marco y el título "VFA-51 GREENIE BOARD" vienen horneados
 * en el skin del chrome (`body.chrome-wood--greenie`).
 */
export function renderBoardShell(): string {
  return `
    <div class="gb-stage">
      <div class="gb-inner" role="region" aria-label="Greenie Board">
        <div class="gb-legend" aria-label="Leyenda de calificaciones">
          <div class="gb-legend-items">${renderLegend()}</div>
          <span class="gb-updated" data-zone="updated">cargando…</span>
        </div>

        <div class="gb-rows" data-zone="rows"></div>
        <p class="gb-empty" data-zone="empty" hidden>
          — Todavía no hay apontajes registrados. —
        </p>
      </div>
    </div>
  `;
}

/**
 * Bloque FILTROS para el sidebar derecho del chrome. Los `<select>`
 * se montan vacíos y los rellena `mount.ts` con las opciones únicas
 * extraídas de los datos cargados; un cambio en la selección filtra
 * los pases visibles del tablero. El contador `[data-zone="filter-info"]`
 * muestra "N PILOTOS · M PASES" tras filtrar.
 */
export function renderFiltersSidebar(): string {
  return `
    <section class="chrome-actions gb-filters">
      <h2 class="chrome-actions-title">FILTROS</h2>
      <p class="gb-filter-row">
        <label class="gb-filter-label" for="gb-filter-campaign">CAMPAÑA</label>
        <select class="gb-filter-select" id="gb-filter-campaign">
          <option value="">— TODAS —</option>
        </select>
      </p>
      <p class="gb-filter-row">
        <label class="gb-filter-label" for="gb-filter-mission">MISIÓN</label>
        <select class="gb-filter-select" id="gb-filter-mission">
          <option value="">— TODAS —</option>
        </select>
      </p>
      <p class="gb-filter-row">
        <span class="gb-filter-label">FECHA</span>
        <span class="gb-filter-daterange">
          <input
            type="date"
            class="gb-filter-date"
            id="gb-filter-date-from"
            aria-label="Fecha desde"
          />
          <span class="gb-filter-date-sep" aria-hidden="true">→</span>
          <input
            type="date"
            class="gb-filter-date"
            id="gb-filter-date-to"
            aria-label="Fecha hasta"
          />
        </span>
      </p>
      <p class="gb-filter-info" data-zone="filter-info">—</p>
      <p class="gb-filter-actions">
        <button type="button" class="gb-filter-clear" id="gb-filter-clear">LIMPIAR</button>
      </p>
    </section>
  `;
}

/** Tira de muestras de color → etiqueta para la leyenda. */
function renderLegend(): string {
  return LEGEND_ORDER.map((grade) => {
    const info = gradeInfo(grade);
    const star = grade === '_OK_' ? '<span class="gb-star">★</span>' : '';
    return `
      <span class="gb-legend-item" title="${esc(info.label)}">
        <span class="gb-legend-swatch ${info.cls}">${star}</span>
        <span class="gb-legend-label">${esc(legendShort(grade))}</span>
      </span>
    `;
  }).join('');
}

/** Etiqueta corta de una calificación (leyenda y selector de edición). */
export function legendShort(grade: string): string {
  switch (grade) {
    case '_OK_':
      return 'OK destacado';
    case 'OK':
      return 'OK';
    case '(OK)':
      return 'Justo';
    case '--':
      return 'Sin nota';
    case 'B':
      return 'Bolter';
    case 'WO':
      return 'Wave-off';
    case 'OWO':
      return 'Own wave-off';
    case 'C':
      return 'Cut';
    default:
      return grade;
  }
}

// ─── Filas de piloto ───────────────────────────────────────────────────

/** Render del cuerpo completo: una fila por piloto. */
export function renderRows(pilots: GreenieBoardPilot[]): string {
  return pilots.map((p, i) => renderPilotRow(p, i)).join('');
}

function renderPilotRow(pilot: GreenieBoardPilot, pilotIdx: number): string {
  const avg =
    pilot.avgGradePoints !== null ? pilot.avgGradePoints.toFixed(2) : '—';
  // Pases en orden cronológico (antiguo → reciente): el primero queda
  // pegado al callsign por la izquierda. CSS alinea la tira a la
  // izquierda; los pilotos con poco historial dejan los huecos a la
  // derecha del último pase, no antes del primero.
  const passes = pilot.passes
    .map((pass, passIdx) => renderPass(pass, pilotIdx, passIdx))
    .join('');
  return `
    <div class="gb-row">
      <div class="gb-cs">
        <span class="gb-cs-name">${esc(pilot.callsign)}</span>
        <span class="gb-cs-stat">
          ${avg} pts · ${pilot.trapCount}/${pilot.totalPasses} traps
        </span>
      </div>
      <div class="gb-strip">${passes}</div>
    </div>
  `;
}

// ─── Cuadro de un pase ─────────────────────────────────────────────────

function renderPass(
  pass: GreeniePass,
  pilotIdx: number,
  passIdx: number,
): string {
  // Una banda por intento — la más antigua arriba, el trap final abajo.
  const bands = pass.attempts
    .map((a) => `<div class="gb-band ${gradeInfo(a.grade).cls}"></div>`)
    .join('');

  // La silueta (negra) usa la aeronave del intento terminal.
  const last = pass.attempts[pass.attempts.length - 1];
  const acUrl = last ? silhouetteUrl(last.aircraft) : null;
  const ac = acUrl
    ? `<span class="gb-ac" style="--gb-ac:url('${acUrl}')"></span>`
    : '<span class="gb-ac-fallback" aria-hidden="true">✈</span>';

  const wire =
    pass.trapped && last && last.wire !== null
      ? `<span class="gb-wire">${String(last.wire)}</span>`
      : '';
  const star =
    pass.terminatingGrade === '_OK_'
      ? '<span class="gb-pass-star">★</span>'
      : '';
  const dateShort = formatDateShort(pass.dateIso);
  const date = dateShort
    ? `<span class="gb-date">${esc(dateShort)}</span>`
    : '';

  return `
    <div
      class="gb-pass"
      role="button"
      tabindex="0"
      data-pilot="${String(pilotIdx)}"
      data-pass="${String(passIdx)}"
      data-mission="${esc(pass.missionReportId)}"
      title="${esc(buildPassTitle(pass))}"
    >
      <div class="gb-bands">${bands}</div>
      ${ac}
      ${star}
      ${date}
      ${wire}
    </div>
  `;
}

/**
 * Texto del tooltip nativo de un pase: misión + fecha y el desglose
 * de intentos. El detalle código a código está en el modal (clic).
 */
function buildPassTitle(pass: GreeniePass): string {
  const head = `${pass.missionName} — ${formatDate(pass.dateIso)}`;
  const lines = pass.attempts.map((a, i) => {
    const info = gradeInfo(a.grade);
    const wire = a.wire !== null ? ` · cable ${String(a.wire)}` : '';
    return `${String(i + 1)}. ${info.label}${wire}`;
  });
  return [head, ...lines].join('\n');
}

// ─── Estados auxiliares ────────────────────────────────────────────────

/** Fila de carga mientras se resuelve el fetch. */
export function renderLoading(): string {
  return '<p class="gb-note">Cargando apontajes…</p>';
}

/** Banner de error dentro del cuerpo del tablero. */
export function renderErrorRow(message: string): string {
  return `<p class="gb-note gb-note--error">${esc(message)}</p>`;
}

// ─── Helpers ───────────────────────────────────────────────────────────

/** Formatea un ISO a `dd/mm/yyyy`; `—` si no es válido. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * Formatea un ISO a `dd/mm/aa` (año a 2 dígitos), pensado para el pie
 * del cuadro de pase. Devuelve `null` si el ISO no es válido para que
 * el caller omita el span entero.
 */
function formatDateShort(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${yy}`;
}

/** Formatea el `generatedAt` para la cabecera (`dd/mm/yyyy hh:mm`). */
export function formatUpdated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'fecha desconocida';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `actualizado ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/` +
    `${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
